import map from 'lodash/map'
import pick from 'lodash/fp/pick'
import size from 'lodash/fp/size'
import orderBy from 'lodash/orderBy'

import {
  all,
  call,
  cancelled,
  fork,
  join,
  put,
  select,
  takeEvery,
  takeLatest
} from 'redux-saga/effects'

import flow from 'lodash/flow'
import sortBy from 'lodash/fp/sortBy'
import mapValues from 'lodash/fp/mapValues'

import { addError } from '../actions/errors'

import {
  DELETE_PROJECT_DATASETS,
  DELETE_PROJECT_FOLDERS,
  deletingProjectDatasets,
  deletingSubProjects,
  LOAD_PROJECT_DATASETS,
  LOAD_PROJECT_DETAILS,
  LOAD_PROJECT_MEMBERS,
  LOAD_PROJECT_PARENT,
  LOAD_PROJECT_ROOT,
  LOAD_PROJECT_SUBPROJECTS,
  LOAD_PROJECTCONTENT,
  loadingProjectDetails,
  loadingProjectInitialTree,
  loadingProjectMembers,
  loadingProjectParent,
  loadingProjectSubprojects,
  loadingProjectSubprojectsTree,
  loadProjectContent,
  MOVE_PROJECT_CONTENT,
  movingContent,
  setProjectContent,
  setProjectDetails,
  setProjectInitialTree,
  setProjectMembers,
  setProjectPagination,
  setProjectParent,
  setProjectPath,
  setProjectRoot,
  setProjectRowsPerPage,
  setProjectSubprojects,
  setProjectSubprojectsTree,
  UPDATE_PROJECT_DATASET,
  UPDATE_PROJECT_DETAILS,
  updatingProjectDataset
} from '../actions/project'

import {
  DELETE_PROJECTS,
  UPDATE_PROJECT,
  updatingProject
} from '../actions/projects'

import {
  delProjectDataset,
  getCapabilities,
  getProject,
  getProjectDatasets,
  getProjectMembers,
  getProjectPath,
  getProjectSubprojects,
  moveDataset,
  putProjectDataset
} from '../apis/project'

import {
  delProject,
  moveProject,
  patchProject,
  putProject
} from '../apis/projects'

import { putProjectMembers } from '../apis/members'

import { linkNotify, notify } from '../helpers/saga'

import {
  getCustomerUsers,
  getProjectContentNamePrefix,
  getProjectContentPagination,
  getProjectContentRowsPerPage,
  getProjectContentSortBy,
  getProjectContentSortOrder,
  getProjectDetails,
  getProjectInitialTree,
  getProjectTree
} from '../state'

const TAG = 'saga/project'

export default function* watchProject() {
  yield takeEvery(UPDATE_PROJECT_DATASET, updateProjectDataset)
  yield takeEvery(DELETE_PROJECT_DATASETS, deleteProjectDatasets)
  yield takeEvery(DELETE_PROJECT_FOLDERS, deleteSubProjects)
  // @ts-ignore
  yield takeEvery(UPDATE_PROJECT_DETAILS, updateProjectDetails)
  yield takeEvery(MOVE_PROJECT_CONTENT, moveContent)
  yield takeLatest(LOAD_PROJECT_DETAILS, loadProjectDetails)
  yield takeLatest(LOAD_PROJECT_DATASETS, loadProjectDatasets)
  yield takeLatest(LOAD_PROJECT_MEMBERS, loadProjectMembers)
  yield takeLatest(LOAD_PROJECT_SUBPROJECTS, loadProjectSubprojects)
  yield takeLatest(LOAD_PROJECT_ROOT, loadProjectRoot)
  yield takeLatest(LOAD_PROJECTCONTENT, loadFoldersAndDatasets)
}

const getSort = sortBy => {
  let sorter = [sortBy]
  const lowerSortBy = sortBy.toLowerCase()

  switch (lowerSortBy) {
    case 'name':
      sorter = [content => content.name.toLowerCase()]
      break
    case 'description':
      sorter = [
        content => content.description && content.description.toLowerCase()
      ]
      break
    case 'datasetformat':
      sorter = [
        content => content.datasetFormat && content.datasetFormat.toLowerCase()
      ]
      break
    case 'datasettype':
      sorter = [
        content => content.datasetType && content.datasetType.toLowerCase()
      ]
      break
    case 'size':
    case 'storagesize':
      sorter = [content => content.storageSize || 0]
      break
    case 'createdat':
      sorter = [content => new Date(content.createdAt).getTime()]
      break
    case 'updatedat':
      sorter = [content => new Date(content.updatedAt).getTime()]
      break
    default:
      break
  }
  return sorter
}

export function* loadFoldersAndDatasets(action) {
  try {
    yield put(loadingProjectSubprojects())
    let projectContent = []
    let count = 0

    const capabilities = yield call(getCapabilities, action.projectId)

    if (capabilities.canListContent) {
      const [projectDatasets, allSubprojects] = yield all([
        call(getProjectDatasets, action.projectId),
        call(getProjectSubprojects, action.projectId)
      ])

      const datasets = projectDatasets.filter(dataset =>
        dataset.name.startsWith(action.namePrefix)
      )
      const subprojects = allSubprojects.filter(sub =>
        sub.name.startsWith(action.namePrefix)
      )

      const content = datasets.concat(subprojects)
      const sortedContent = orderBy(
        content,
        getSort(action.sortBy.toLowerCase()),
        [action.sortOrder.toLowerCase()]
      )
      count = sortedContent.length
      projectContent = sortedContent
    }

    yield put(
      setProjectContent(
        projectContent,
        count,
        action.rowsPerPage,
        action.page,
        action.namePrefix,
        action.sortBy,
        action.sortOrder,
        action.projectId
      )
    )
  } catch (error) {
    yield put(addError(LOAD_PROJECTCONTENT, error))
  } finally {
    yield put(loadingProjectSubprojects(false))
  }
}

export function* loadProjectMembers(action) {
  try {
    yield put(loadingProjectMembers())

    const rawMembers = yield call(getProjectMembers, action.projectId)
    const users = yield select(getCustomerUsers)
    const members = enhanceMembers(users)(rawMembers)

    yield put(setProjectMembers(members))
  } catch (error) {
    yield put(addError(LOAD_PROJECT_MEMBERS, error))
  } finally {
    yield put(loadingProjectMembers(false))
  }
}

export function* updateProjectDataset(action) {
  const { projectId, data } = action
  const page = yield select(getProjectContentPagination)
  const rowsPerPage = yield select(getProjectContentRowsPerPage)
  const namePrefix = yield select(getProjectContentNamePrefix)
  const sortBy = yield select(getProjectContentSortBy)
  const sortOrder = yield select(getProjectContentSortOrder)

  try {
    yield put(updatingProjectDataset())

    const dataset = yield call(putProjectDataset, projectId, data)

    yield fork(notify, `The dataset "${dataset.name}" was updated!`)
  } catch (error) {
    yield put(addError(UPDATE_PROJECT_DATASET, error))
  } finally {
    yield put(updatingProjectDataset(false))
    yield put(
      loadProjectContent(
        projectId,
        page * rowsPerPage,
        rowsPerPage,
        sortBy,
        sortOrder,
        namePrefix
      )
    )
  }
}

function* settled(fn, ...args) {
  try {
    return { ok: true, value: yield call(fn, ...args) }
  } catch (error) {
    return { ok: false, error }
  }
}

export function* loadPathLeg(projectId) {
  const path = yield call(settled, getProjectPath, projectId)
  yield put(setProjectPath(path.ok ? path.value : null, projectId))
  return path
}

export function* loadDetailsLeg(projectId) {
  const [capabilities, details] = yield all([
    call(settled, getCapabilities, projectId),
    call(settled, getProject, projectId)
  ])

  yield put(
    capabilities.ok && details.ok
      ? setProjectDetails(
          { ...details.value, capabilities: capabilities.value },
          capabilities.value
        )
      : setProjectDetails(null, null)
  )

  return [capabilities, details]
}

export function* loadProjectDetails(action) {
  const projectId = action.id
  if (projectId) {
    try {
      yield put(loadingProjectDetails(true, projectId))

      const pathTask = yield fork(loadPathLeg, projectId)
      const detailsTask = yield fork(loadDetailsLeg, projectId)

      const [path, [capabilities, details]] = yield all([
        join(pathTask),
        join(detailsTask)
      ])

      // Three legs failing the same way is one problem, not three toasts.
      const reported = new Set<string>()

      for (const { ok, error } of [path, capabilities, details]) {
        if (ok) continue

        const key = `${error?.httpStatus ?? ''}|${error?.message ?? error}`
        if (reported.has(key)) continue

        reported.add(key)
        yield put(addError(LOAD_PROJECT_DETAILS, error))
      }
    } catch (error) {
      yield put(addError(LOAD_PROJECT_DETAILS, error))
    } finally {
      // A cancelled run still resets paging: the run superseding it (takeLatest)
      // may be a refresh that deliberately keeps paging, so the reset this
      // navigation asked for would otherwise be lost.
      if (action.resetPaging !== false) {
        yield put(setProjectPagination(0))
        yield put(setProjectRowsPerPage(25))
      }
      // But it leaves `loading` to the newer run: its put interleaves with the
      // newer run's and would clear `loading` while that request is in flight.
      if (!(yield cancelled())) {
        yield put(loadingProjectDetails(false, projectId))
      }
    }
  }
}

export function* loadProjectDatasets(action) {
  const page = yield select(getProjectContentPagination)
  const rowsPerPage = yield select(getProjectContentRowsPerPage)
  const namePrefix = yield select(getProjectContentNamePrefix)
  const sortBy = yield select(getProjectContentSortBy)
  const sortOrder = yield select(getProjectContentSortOrder)
  yield put(
    loadProjectContent(
      action.projectId,
      page * rowsPerPage,
      rowsPerPage,
      sortBy,
      sortOrder,
      namePrefix
    )
  )
}

export function* loadProjectSubprojects(action) {
  try {
    action.forTable
      ? yield put(loadingProjectSubprojects())
      : yield put(loadingProjectSubprojectsTree())

    const subProjects = yield call(getProjectSubprojects, action.projectId)

    if (action.forTable) {
      yield put(setProjectSubprojects(subProjects))
    } else {
      const tree = yield select(getProjectTree)
      yield put(setProjectSubprojectsTree(subProjects, action.projectId, tree))
    }
  } catch (error) {
    yield put(addError(LOAD_PROJECT_SUBPROJECTS, error))
  } finally {
    yield put(loadingProjectSubprojects(false))
    yield put(loadingProjectSubprojectsTree(false))
  }
}

export function* loadProjectParent() {
  try {
    const project = yield select(getProjectDetails)
    if (project && project.parentProjectId) {
      yield put(loadingProjectParent())
      const parentProject = yield call(getProject, project.parentProjectId)
      yield put(setProjectParent(parentProject))
    } else {
      yield put(setProjectParent(null))
    }
  } catch (error) {
    yield put(addError(LOAD_PROJECT_PARENT, error))
  } finally {
    yield put(loadingProjectParent(false))
  }
}

export function* loadProjectRoot(action) {
  try {
    let { project } = action

    yield put(loadingProjectInitialTree())

    if (!project) return

    if (project.parentProjectId) {
      const parentProject = yield call(getProject, project.parentProjectId)
      const subProjects = yield call(
        getProjectSubprojects,
        project.parentProjectId
      )
      const initialtree = yield select(getProjectInitialTree)
      const newTree = { ...initialtree, [parentProject.id]: subProjects }
      yield put(setProjectInitialTree(newTree))
      project = parentProject
      yield call(loadProjectRoot, { project })
    } else {
      const initialtree = yield select(getProjectInitialTree)
      const newTree = { ...initialtree, project: [project] }
      yield put(setProjectRoot(newTree))
    }
  } catch (error) {
    yield put(addError(LOAD_PROJECT_PARENT, error))
  } finally {
    yield put(loadingProjectInitialTree(false))
  }
}

export function* deleteProjectDatasets(action) {
  const { projectId, datasetIds } = action
  const page = yield select(getProjectContentPagination)
  const rowsPerPage = yield select(getProjectContentRowsPerPage)
  const namePrefix = yield select(getProjectContentNamePrefix)
  const sortBy = yield select(getProjectContentSortBy)
  const sortOrder = yield select(getProjectContentSortOrder)

  try {
    yield put(deletingProjectDatasets())

    yield all(datasetIds.map(datasetId => call(delProjectDataset, datasetId)))

    yield fork(
      notify,
      datasetIds.length > 1
        ? `${datasetIds.length} datasets were deleted!`
        : 'The dataset was deleted!'
    )
  } catch (error) {
    yield put(addError(DELETE_PROJECT_DATASETS, error))
  } finally {
    yield put(deletingProjectDatasets(false))
    yield put(
      loadProjectContent(
        projectId,
        page * rowsPerPage,
        rowsPerPage,
        sortBy,
        sortOrder,
        namePrefix
      )
    )
  }
}

const enhanceMembers = users =>
  flow(
    mapValues(member => {
      const user = users[member.userId]
      if (!user) {
        // eslint-disable-next-line no-console
        console.warn(TAG, `User not found with id "${member.userId}"!`)
      }
      return {
        ...user,
        ...member
      }
    }),
    sortBy('displayName')
  )

export function* deleteSubProjects(action) {
  const { projectParentId, projectIds } = action
  const projectId = projectParentId
  const page = yield select(getProjectContentPagination)
  const rowsPerPage = yield select(getProjectContentRowsPerPage)
  const namePrefix = yield select(getProjectContentNamePrefix)
  const sortBy = yield select(getProjectContentSortBy)
  const sortOrder = yield select(getProjectContentSortOrder)

  try {
    yield put(deletingSubProjects())

    yield all(projectIds.map(id => call(delProject, id)))

    yield fork(
      notify,
      projectIds.length > 1
        ? `${projectIds.length} folders were deleted!`
        : 'The folder was deleted!'
    )
  } catch (error) {
    yield put(addError(DELETE_PROJECTS, error))
  } finally {
    yield put(deletingSubProjects(false))
    yield put(
      loadProjectContent(
        projectId,
        page * rowsPerPage,
        rowsPerPage,
        sortBy,
        sortOrder,
        namePrefix
      )
    )
  }
}

export function* moveContent(action) {
  const { targetProjectId, projectIds, datasetIds, projectId } = action

  const page = yield select(getProjectContentPagination)
  const rowsPerPage = yield select(getProjectContentRowsPerPage)
  const namePrefix = yield select(getProjectContentNamePrefix)
  const sortBy = yield select(getProjectContentSortBy)
  const sortOrder = yield select(getProjectContentSortOrder)
  try {
    yield put(movingContent())

    yield all(projectIds.map(id => call(moveProject, id, targetProjectId)))
    yield all(datasetIds.map(id => call(moveDataset, id, targetProjectId)))

    const project = yield call(getProject, targetProjectId)
    const selectionSize = size(projectIds) + size(datasetIds)
    let message = selectionSize > 1 ? 'Selected items' : 'Selected item'
    message += ' successfully moved to ' + project.name

    yield fork(linkNotify, message, project)
  } catch (error) {
    yield put(addError(MOVE_PROJECT_CONTENT, error))
  } finally {
    yield put(movingContent(false))
    yield put(
      loadProjectContent(
        projectId,
        page * rowsPerPage,
        rowsPerPage,
        sortBy,
        sortOrder,
        namePrefix
      )
    )
  }
}

export function* updateProjectDetails({ data, projectDidUpdate }) {
  const { members, accessLevel, parentProjectIdToReload, ...projectData } = data

  const updatedMembers = map(members, pick(['userId', 'role']))

  const {
    id,
    name,
    capabilities: { canEditAccessLevel, canGrantAccess, canEdit },
    rowVersion
  } = projectData

  try {
    yield put(updatingProject())
    let versionId = rowVersion
    if (canEdit) {
      const response = yield call(putProject, projectData)
      if (response && response.rowVersion) {
        versionId = response.rowVersion
      }
    }

    if (canEditAccessLevel) {
      const response = yield call(patchProject, id, { accessLevel }, versionId)
      if (response && response.rowVersion) {
        versionId = response.rowVersion
      }
    }

    if (canGrantAccess) {
      yield call(putProjectMembers, id, updatedMembers, versionId)
    }

    yield fork(notify, `Project "${name}" was updated!`)
  } catch (error) {
    // @ts-ignore
    if (error['httpStatus'] === 409) {
      yield put(
        addError(
          UPDATE_PROJECT,
          'Somebody else was updating the project at the same time, you can refresh your page to see the latest changes.'
        )
      )
    } else {
      yield put(addError(UPDATE_PROJECT, error))
    }
  } finally {
    yield put(updatingProject(false))
    // If we're editing a subfolder, reload the parent project's details
    // Otherwise reload the edited project's details
    yield call(loadProjectDetails, { id: parentProjectIdToReload || id })
    if (projectDidUpdate) {
      yield call(projectDidUpdate)
    }
  }
}
